import React from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { ArrowUpRight } from "lucide-react";

/* ─── Service Data ───────────────────────────────────────────── */
const SERVICES = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
      </svg>
    ),
    title: "Branding",
    subtitle: "Build a strong and memorable identity for your brand.",
    items: ["Logo design", "Brand identity system", "Colors, typography, visual language"],
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
      </svg>
    ),
    title: "UI / UX & Website",
    subtitle: "Design modern, clean & conversion focused digital experiences.",
    items: ["Website design", "Website redesign", "Conversion-focused UI", "Development"],
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
      </svg>
    ),
    title: "Graphic Design",
    subtitle: "Creative visuals that make your brand stand out",
    items: ["Posters & flyers", "Social media creatives", "Hoardings & ad creatives"],
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      </svg>
    ),
    title: "Packaging Design",
    subtitle: "Product designs that attract and influence buying decisions.",
    items: ["Product labels", "Packaging systems"],
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
      </svg>
    ),
    title: "Social Media Management",
    subtitle: "Complete handling of your social presence.",
    items: ["Content planning", "Creative design", "Captions & hashtags", "Monthly posting"],
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
    title: "SEO",
    subtitle: "Improve your online visibility and search presence.",
    items: ["Business profile optimization", "Local SEO", "Basic website SEO", "Development"],
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    title: "Performance Marketing",
    subtitle: "Run targeted campaigns to generate leads & sales.",
    items: ["Meta Ads (Insta & Fb )", "Google Ads", "Campaign setup & optimization"],
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 110, damping: 18 } },
};

/* ─── Service Card ───────────────────────────────────────────── */
const ServiceCard = ({ icon, title, subtitle, items }) => (
  <motion.div variants={cardVariants} className="svc-card">
    <div className="svc-icon">{icon}</div>
    <h3 className="svc-title">{title}</h3>
    <div class="color-border "></div>
    <p className="svc-subtitle">{subtitle}</p>
    <ul className="svc-list">
      {items.map((item, i) => (
        <li key={i} className="svc-item">
          <span className="svc-check">✓</span>
          {item}
        </li>
      ))}
    </ul>
  </motion.div>
);

/* ─── Main Section ───────────────────────────────────────────── */
const ServicesSection = () => (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

      .svc-section {
        background: #ffffff;
        padding: 72px 48px 80px;
        font-family: 'Poppins', sans-serif;
        gap: 40px;
        padding-left: 32px;
        padding-right: 32px;
        max-width: 80rem;
        margin: 0 auto;
      }
.color-border{
width: 70%;
      margin: 6px 0 10px;
  border-top: 2px solid;
  border-image: linear-gradient(
    90deg,
    #129ADC,
    #B55DD0,
    #E154AA,
    #F45A39
  ) 1;
}

      /* ── Top row ── */
      .svc-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 16px;
        margin-bottom: 48px;
      }

      .svc-label {
        font-size:20px;
          font-weight: 600;
          line-height: 28px;
          letter-spacing: 0em;
          text-transform: uppercase;
          background: linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
          width: fit-content;
      }

      .svc-heading {
        font-size: 36px;
          font-weight: 700;
          color: #111827;
              line-height: 52px;
          margin: 0 0 24px;
        max-width: 520px;
      }

      .svc-cta {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-family: 'Poppins', sans-serif;
        font-size: 16px;
        font-weight: 600;
        color: #111;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        margin-top: 8px;
        white-space: nowrap;
        transition: opacity 0.2s;
      }
      .svc-cta:hover { opacity: 0.65; }

      /* ── Cards grid ── */
      .svc-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 24px;
      }
      @media(max-width: 1024px){ .svc-grid{ grid-template-columns: repeat(2, 1fr); } }
      @media(max-width: 560px){ .svc-grid{ grid-template-columns: 1fr; } }

      .svc-card {
        background: #f4f4f4;
        border-radius: 16px;
        padding: 24px 34px 22px;
        display: flex;
        flex-direction: column;
        gap: 0;
        transition: box-shadow 0.22s ease, transform 0.22s ease;
      }
      .svc-card:hover {
        box-shadow: inset 9px 10px 20px 14px rgb(0 0 0 / 8%);
      }

      .svc-icon {
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border-radius: 100%;
        margin-bottom: 14px;
        box-shadow: 0 1px 6px rgba(0,0,0,0.07);
      }

      .svc-title {
        font-size: 15px;
        font-weight: 700;
        color: #111;
        margin: 0 0 6px;
        line-height: 1.3;
        // text-decoration: underline;
        text-underline-offset: 3px;
        text-decoration-thickness: 1px;
      }

      .svc-subtitle {
        font-size: 13px;
        color: #6B7280;
        line-height: 1.6;
        margin: 0 0 14px;
      }

      .svc-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 5px;
      }

      .svc-item {
        font-size: 12.5px;
        color: #374151;
        display: flex;
        align-items: flex-start;
        gap: 7px;
        line-height: 1.5;
      }

      .svc-check {
        color: #129ADC;
        font-size: 0.7rem;
        margin-top: 1px;
        flex-shrink: 0;
      }

      /* ── Bottom buttons ── */
      .svc-btns {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 16px;
        margin-top: 48px;
        flex-wrap: wrap;
      }

      .svc-btn-outline {
        padding: 11px 32px;
        border-radius: 100px;
        border: 1.5px solid transparent;
        background: linear-gradient(#fff, #fff) padding-box,
                    linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39) border-box;
        font-family: 'Poppins', sans-serif;
        font-size: 0.82rem;
        font-weight: 500;
        cursor: pointer;
        transition: box-shadow 0.2s;
      }
      .svc-btn-outline:hover { box-shadow: 0 4px 18px rgba(181,93,208,0.2); }
      .svc-btn-outline span {
        background: linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .svc-btn-text {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-family: 'Poppins', sans-serif;
        font-size: 0.82rem;
        font-weight: 600;
        color: #111;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        transition: opacity 0.2s;
      }
      .svc-btn-text:hover { opacity: 0.65; }
    `}</style>

    <section className="svc-section">

      {/* Top row: label + heading + CTA */}
      <div className="svc-top">
        <div>
          <span className="svc-label">ALL SERVICES</span>
          <h2 className="svc-heading">
            Making brands look cooler<br />than founders
          </h2>
        </div>
        
        <button className="svc-cta" onClick={() => window.location.href = "#contact"}>
          Get a Free Review <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>

      {/* Cards */}
      <motion.div
        className="svc-grid"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {SERVICES.map((svc, i) => (
          <ServiceCard key={i} {...svc} />
        ))}
      </motion.div>

      {/* Bottom buttons */}
      <div className="svc-btns">
        <motion.button
          className="svc-btn-outline"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => window.location.href = "whatsapp://send?phone=+91 9491105026&text=Hello%20Sintx!%20I%20would%20like%20to%20request%20a%20quote."}
        >
          <span>Request a Quote</span>
        </motion.button>
        <motion.button
          className="svc-btn-text"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => window.location.href = "/portfolio"}
        >
          View Portfolio <ArrowUpRight className="w-4 h-4" />
        </motion.button>
      </div>

    </section>
  </>
);

export default ServicesSection;